import { getStringHash } from '@noths/polaris-client-utils';
import {
  Environment as ContentStackEnvs,
  fetchContentGraphQL,
  stackConfig,
} from '@noths/polaris-server-content-fetching';
import type bunyan from 'bunyan';

import { adaptGraphQLGlobalNavToCommonNav } from './graphql/adaptGraphQLGlobalNavToCommonNav';
import { adaptGraphQLTopBannerToCommon } from './graphql/adaptGraphQLTopBannerToCommon';
import type { ContentstackGlobalNavGraphQLData } from './graphql/contentstackGraphQLResponse';
import { globalNavigationAndTopBannerQuery } from './graphql/globalNavigationAndTopBannerQuery';
import type { NavigationProps } from 'client-site-chrome-ui/components/organisms/Navigation/Navigation';
import type { DiscountPresentationMode } from 'client-site-chrome-ui/content/types/DiscountPresentationMode';
import { toDiscountPresentationMode } from 'client-site-chrome-ui/content/types/DiscountPresentationMode';
import type { TopBannerMessage } from 'client-site-chrome-ui/content/types/TopBannerMessage';

export const Environment = ContentStackEnvs;

export const GLOBAL_NAVIGATION_QUERY_HASH = getStringHash(globalNavigationAndTopBannerQuery);

interface FetchNavigationConfig {
  credentials?: { apiKey: string; token: string };
  env: ContentStackEnvs;
  logger?: bunyan;
}

export interface SiteChromeContent {
  /**
   * Optional because this interface is built by consumers outside this repo, where a
   * required property would be a breaking change. Absent means 'offer'.
   */
  discountPresentationMode?: DiscountPresentationMode;
  navContent: NavigationProps['content'];
  topBannerMessages: TopBannerMessage[];
}

export const fetchSiteChromeContent = async (
  { credentials, env, logger }: FetchNavigationConfig,
  variantId = 'control',
): Promise<SiteChromeContent> => {
  const { apiKey, token } = credentials ?? {
    apiKey: stackConfig.DX.apiKey,
    token: stackConfig.DX.envs[env].token,
  };

  const result = await fetchContentGraphQL(
    {
      deliveryToken: token,
      environment: stackConfig.DX.envs[env].name,
      query: globalNavigationAndTopBannerQuery,
      stackAPIKey: apiKey,
      variables: {
        variantId,
      },
    },
    {
      cacheKey: `global-nav-${variantId}-${GLOBAL_NAVIGATION_QUERY_HASH}`,
      cacheTTLInSeconds: 300,
      timeout: 2000,
    },
  );

  if (logger && result.status === 'expired') {
    logger.warn('fetched navigation content is expired');
  }

  if (result.content) {
    const content = result.content as ContentstackGlobalNavGraphQLData;

    const navContent = content.all_global_navigation_v2.items[0];
    if (navContent) {
      return {
        discountPresentationMode: toDiscountPresentationMode(navContent.discount_presentation_mode),
        topBannerMessages: adaptGraphQLTopBannerToCommon(
          navContent.top_banner_referenceConnection ?? { edges: [] },
        ),
        navContent: adaptGraphQLGlobalNavToCommonNav(navContent),
      };
    }
  }

  if (logger) {
    logger.error('no navigation content received');
  }

  throw new Error('no navigation data!');
};
